import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { CheckTick, ChevronDown, SearchIcon } from '../../../components/icons';
import { INDUSTRIES } from '../constants';

interface IndustryDropdownProps {
  value: string;
  onChange: (id: string) => void;
}

export function IndustryDropdown({ value, onChange }: IndustryDropdownProps): React.ReactElement {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    if (open) document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const selectedLabel = value ? t('onboarding.industries.' + value + '.label') : '';
  const q = query.trim().toLowerCase();
  const filtered = q
    ? INDUSTRIES.filter((i) => (t('onboarding.industries.' + i.id + '.label') as string).toLowerCase().includes(q))
    : INDUSTRIES;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full bg-white rounded-lg px-3.5 py-3 border-2 flex items-center justify-between text-[14px] transition-colors',
          value ? 'border-ink text-ink' : 'border-stroke text-[#334155] hover:border-helper'
        )}
      >
        <span>{selectedLabel || t('onboarding.industryPlaceholder')}</span>
        {value ? (
          <CheckTick className="w-[18px] h-[18px] text-brand" />
        ) : (
          <ChevronDown className="w-[18px] h-[18px] text-ink" />
        )}
      </button>
      {open && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border-2 border-ink rounded-lg overflow-hidden shadow-lg">
          <div className="relative border-b border-stroke">
            <SearchIcon className="absolute left-3 top-3 w-[18px] h-[18px] text-[#334155]" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('onboarding.searchPlaceholder')}
              className="w-full pl-10 pr-3 py-2.5 text-[14px] text-ink placeholder-[#334155] border-none focus:outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-4 py-3 text-[14px] text-[#334155]">{t('onboarding.noMatches')}</div>
            )}
            {filtered.map((i) => (
              <button
                key={i.id}
                type="button"
                onClick={() => {
                  onChange(i.id);
                  setQuery('');
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-[14px] text-ink flex items-center justify-between hover:bg-[#F1F5F9] transition-colors"
              >
                <span>{t('onboarding.industries.' + i.id + '.label')}</span>
                {value === i.id && <CheckTick className="w-[16px] h-[16px] text-brand" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
